import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import {
  Box,
  Typography,
  Card,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Person as PersonIcon,
  ExpandMore,
  ExpandLess,
  AutoAwesome,
  Info as InfoOutlined,
  VolumeUp,
  ArrowRight as ArrowRightIcon,
} from "@mui/icons-material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import HtmlRenderer from "./HtmlRenderer";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import ExplanationBlock from "./ExplanationBlock";

/* ---------------------------------------------------------------
   Brand Colors (Only white and red)
--------------------------------------------------------------- */
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";
const SUCCESS_GREEN = "#4caf50";
const SUCCESS_BG = "#e8f5e9";

/* ---------------------------------------------------------------
   HTML Entity Decoder
--------------------------------------------------------------- */
function decodeHtmlEntities(text) {
  if (!text) return "";
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
}

/* ---------------------------------------------------------------
   Utility Functions
--------------------------------------------------------------- */
function normalizeHeading(raw) {
  if (!raw) return "";
  const parts = String(raw)
    .split(/\r?\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const joined = parts.join(" – ");
  return joined.replace(/\s{2,}/g, " ").trim();
}

function cleanQuestionText(raw = "") {
  return String(raw).replace(/\s+/g, " ").trim();
}

// Build playlist from the test data structure
function buildPlaylist(listeningData = {}) {
  const playlist = [];

  // Handle new IELTS format with sections array (supports both sections and listeningSections)
  const sectionsArray = listeningData?.sections || listeningData?.listeningSections;
  if (sectionsArray && Array.isArray(sectionsArray)) {
    sectionsArray.forEach((section) => {
      const questions = section.questions || [];

      // Sort questions by order or questionId to maintain correct sequence
      const sortedQuestions = [...questions].sort((a, b) => {
        // First try to sort by order field
        if (a.order != null && b.order != null) {
          return a.order - b.order;
        }
        // Fallback to questionId if order is not available
        if (a.questionId != null && b.questionId != null) {
          return a.questionId - b.questionId;
        }
        return 0;
      });

      // Group questions by question type and range WITHIN THIS SECTION
      const grouped = new Map();

      sortedQuestions.forEach((question) => {
        const qType = question.questionType;
        const qRange = question.questionRange || question.part || "default";
        const key = `${qType}_${qRange}`;

        if (!grouped.has(key)) {
          grouped.set(key, {
            type: qType,
            range: qRange,
            part: question.part,
            questions: [],
            audioURL: section.audioUrl,
            extractName: section.title || `Section ${section.order}`,
            sectionTitle: section.title, // Preserve section title
            sectionOrder: section.order, // Preserve section order
            instructions: question.instructions || section.instructions,
            startRange: question.startQuestionRange,
            endRange: question.endQuestionRange,
          });
        }

        grouped.get(key).questions.push(question);
      });

      // Convert grouped questions to playlist items
      grouped.forEach((group) => {
        if (
          group.type === "notes_completion" ||
          group.type === "form_completion" ||
          group.type === "table_completion" ||
          group.type === "flowchart_completion" ||
          group.type === "flowchart_completion_select"
        ) {
          // Handle notes/form/table/flowchart completion (Part A style)
          const byHeading = new Map();
          const questionIds = [];

          group.questions.forEach((question) => {
            const heading = normalizeHeading(
              question.questionHeading ||
                question.questionMainHeading ||
                question.heading ||
                "",
            );

            if (!byHeading.has(heading)) byHeading.set(heading, []);

            const qid = question.questionId;
            const rawTxt = question.question || "";
            const txt = cleanQuestionText(rawTxt);
            const inferBlank =
              qid != null || /\(\d+\)/.test(rawTxt) || /_{2,}/.test(rawTxt);

            byHeading.get(heading).push({
              questionId: qid,
              text: txt,
              hasBlank: inferBlank,
              correctAnswer:
                question.correctAnswers?.[0] || question.correctAnswer,
            });

            if (qid != null) {
              questionIds.push(Number(qid));
            }
          });

          const sections = Array.from(byHeading.entries()).map(
            ([heading, notes]) => ({
              heading,
              notes: notes.sort((a, b) => {
                // Sort notes by questionId to maintain correct order
                if (a.questionId != null && b.questionId != null) {
                  return a.questionId - b.questionId;
                }
                return 0;
              }),
            }),
          );

          // Extract patient name if available
          let patientName = "";
          const patientSec = sections.find((s) =>
            /^patient\b/i.test(s.heading),
          );
          if (patientSec?.notes?.length) {
            const nameNote = patientSec.notes.find(
              (n) => !n.hasBlank && n.text?.trim(),
            );
            patientName = (nameNote?.text || "").trim();
          }

          // Check if this is table_completion/flowchart_completion/flowchart_completion_select and extract table data
          const isTableCompletion = group.type === "table_completion";
          const isFlowchartCompletion = group.type === "flowchart_completion" || group.type === "flowchart_completion_select";
          const tableData = isTableCompletion
            ? group.questions.find((q) => q.table)?.table
            : null;
          const questionMainHeading =
            group.questions[0]?.questionMainHeading || "";
          const features = isFlowchartCompletion
            ? group.questions[0]?.features || ""
            : "";

          playlist.push({
            part: group.part || "A",
            type: isTableCompletion
              ? "tableCompletion"
              : isFlowchartCompletion
                ? "flowchartCompletion"
                : group.type === "form_completion"
                  ? "formCompletion"
                  : "noteCompletion",
            audioURL: group.audioURL,
            extractName: group.extractName,
            sectionTitle: group.sectionTitle,
            sectionOrder: group.sectionOrder,
            instructions: group.instructions || "",
            content: { sections },
            questionIds,
            patientName,
            tableData,
            questionMainHeading,
            features,
            questions: group.questions, // Keep original questions for table/form rendering
          });
        } else if (group.type === "mcq_single") {
          // Handle single MCQ questions (Part B/C style)
          // Determine part based on question range or context, default to B
          const determinedPart = group.part === "C" ? "C" : "B";
          group.questions.forEach((question) => {
            playlist.push({
              part: determinedPart,
              type: "mcqSingle",
              sectionTitle: group.sectionTitle,
              sectionOrder: group.sectionOrder,
              audioURL: group.audioURL,
              extractName: group.extractName,
              instructions: group.instructions || "",
              content: {
                questionId: question.questionId,
                question: question.question || "",
                options: question.options || [],
                correctOption:
                  question.correctAnswers?.[0] || question.correctAnswer,
                correctAnswer:
                  question.correctAnswers?.[0] || question.correctAnswer,
              },
            });
          });
        } else if (group.type === "mcq_multiple") {
          // Group mcq_multiple questions by questionRange or by same question text + options
          const groupedByRange = new Map();
          group.questions.forEach((question) => {
            const rangeKey = question.questionRange || "";
            const questionKey = `${question.question || ""}_${JSON.stringify(question.options || [])}`;
            const groupKey = rangeKey || questionKey;

            if (!groupedByRange.has(groupKey)) {
              groupedByRange.set(groupKey, []);
            }
            groupedByRange.get(groupKey).push(question);
          });

          // Add grouped questions to playlist
          groupedByRange.forEach((questions, groupKey) => {
            if (questions.length > 1) {
              // Multiple questions with same range - this is MCQ multiple (choose TWO answers)
              // Combine all correct answers from all questions in the group
              const allCorrectAnswers = questions.flatMap(
                (q) =>
                  q.correctAnswers ||
                  (q.correctAnswer ? [q.correctAnswer] : []),
              );

              playlist.push({
                part: "C",
                type: "mcqMultiple", // Changed from mcqSingle to mcqMultiple
                audioURL: group.audioURL,
                extractName: group.extractName,
                sectionTitle: group.sectionTitle,
                sectionOrder: group.sectionOrder,
                instructions: group.instructions || "",
                isGrouped: true,
                questionIds: questions.map((q) => q.questionId),
                groupedQuestions: questions,
                content: {
                  questionId: questions[0].questionId,
                  question: questions[0].question || "",
                  options: questions[0].options || [],
                  correctOption: allCorrectAnswers.join(", "), // Join all correct answers
                  correctAnswer: allCorrectAnswers.join(", "),
                },
              });
            } else {
              // Single question - add as normal
              const question = questions[0];
              playlist.push({
                part: "C",
                type: "mcqSingle",
                audioURL: group.audioURL,
                extractName: group.extractName,
                sectionTitle: group.sectionTitle,
                sectionOrder: group.sectionOrder,
                instructions: group.instructions || "",
                content: {
                  questionId: question.questionId,
                  question: question.question || "",
                  options: question.options || [],
                  correctOption:
                    question.correctAnswers?.[0] || question.correctAnswer,
                  correctAnswer:
                    question.correctAnswers?.[0] || question.correctAnswer,
                },
              });
            }
          });
        } else if (group.type === "matching_feature") {
          // Handle matching feature questions - assign to Part B
          group.questions.forEach((question) => {
            playlist.push({
              part: "B",
              type: "matchingFeature",
              audioURL: group.audioURL,
              extractName: group.extractName,
              sectionTitle: group.sectionTitle,
              sectionOrder: group.sectionOrder,
              instructions: question.instructions || group.instructions || "",
              features: question.features || group.features || "",
              content: {
                questionId: question.questionId,
                question: question.question || "",
                options: question.options || [],
                correctOption:
                  question.correctAnswers?.[0] || question.correctAnswer,
                correctAnswer:
                  question.correctAnswers?.[0] || question.correctAnswer,
              },
            });
          });
        } else if (group.type === "map_labelling") {
          // Handle map labelling questions - dropdown selection
          group.questions.forEach((question) => {
            playlist.push({
              part: group.part || "B",
              type: "mapLabelling",
              audioURL: group.audioURL,
              extractName: group.extractName,
              sectionTitle: group.sectionTitle,
              sectionOrder: group.sectionOrder,
              instructions: group.instructions || "",
              questionMainHeading:
                question.questionMainHeading || group.questionMainHeading || "",
              imageUrl: question.imageUrl,
              content: {
                questionId: question.questionId,
                question: question.question || "",
                options: question.options || [],
                correctOption:
                  question.correctAnswers?.[0] || question.correctAnswer,
                correctAnswer:
                  question.correctAnswers?.[0] || question.correctAnswer,
              },
            });
          });
        } else if (group.type === "matching_information") {
          // Handle matching information questions - dropdown selection
          group.questions.forEach((question) => {
            playlist.push({
              part: group.part || "C",
              type: "matchingInformation",
              audioURL: group.audioURL,
              extractName: group.extractName,
              sectionTitle: group.sectionTitle,
              sectionOrder: group.sectionOrder,
              instructions: question.instructions || group.instructions || "",
              features: question.features || group.features || "",
              questionMainHeading:
                question.questionMainHeading || group.questionMainHeading || "",
              content: {
                questionId: question.questionId,
                question: question.question || "",
                options: question.options || [],
                correctOption:
                  question.correctAnswers?.[0] || question.correctAnswer,
                correctAnswer:
                  question.correctAnswers?.[0] || question.correctAnswer,
              },
            });
          });
        }
      });
    });

    return playlist;
  }

  // Handle API format with parts array (from test results)
  if (listeningData?.parts && Array.isArray(listeningData.parts)) {
    listeningData.parts.forEach((part) => {
      const partType = part.part?.toUpperCase();

      if (partType === "A" && part.extracts) {
        part.extracts.forEach((extract, extractIndex) => {
          if (extract.questions && extract.questions.length > 0) {
            // Sort questions by order or questionId
            const sortedQuestions = [...extract.questions].sort((a, b) => {
              if (a.order != null && b.order != null) {
                return a.order - b.order;
              }
              if (a.questionId != null && b.questionId != null) {
                return a.questionId - b.questionId;
              }
              return 0;
            });

            const byHeading = new Map();
            const questionIds = [];

            sortedQuestions.forEach((question) => {
              const heading = normalizeHeading(question.heading);
              if (!byHeading.has(heading)) byHeading.set(heading, []);

              const qid = question.questionId;
              const rawTxt = question.question || "";
              const txt = cleanQuestionText(rawTxt);
              const inferBlank =
                qid != null || /\(\d+\)/.test(rawTxt) || /_{2,}/.test(rawTxt);
              const hasBlank = inferBlank;

              byHeading.get(heading).push({
                questionId: qid,
                text: txt,
                hasBlank,
                correctAnswer: question.correctAnswer,
              });

              if (qid != null) {
                questionIds.push(Number(qid));
              }
            });

            const sections = Array.from(byHeading.entries()).map(
              ([heading, notes]) => ({
                heading,
                notes: notes.sort((a, b) => {
                  if (a.questionId != null && b.questionId != null) {
                    return a.questionId - b.questionId;
                  }
                  return 0;
                }),
              }),
            );

            let patientName = "";
            const patientSec = sections.find((s) =>
              /^patient\b/i.test(s.heading),
            );
            if (patientSec?.notes?.length) {
              const nameNote =
                patientSec.notes.find((n) => !n.hasBlank && n.text?.trim()) ||
                patientSec.notes.find((n) => n.text?.trim()) ||
                patientSec.notes[0];
              patientName = (nameNote?.text || "").trim();
            }

            playlist.push({
              part: "A",
              type: "noteCompletion",
              audioURL: extract.audioUrl || extract.audioURL || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              content: { sections },
              questionIds,
              patientName,
            });
          }
        });
      }

      if (partType === "B" && part.extracts) {
        part.extracts.forEach((extract, extractIndex) => {
          if (extract.questions && extract.questions.length > 0) {
            // Sort questions by order or questionId
            const sortedQuestions = [...extract.questions].sort((a, b) => {
              if (a.order != null && b.order != null) {
                return a.order - b.order;
              }
              if (a.questionId != null && b.questionId != null) {
                return a.questionId - b.questionId;
              }
              return 0;
            });

            sortedQuestions.forEach((question) => {
              playlist.push({
                part: "B",
                type: "mcqSingle",
                audioURL: extract.audioUrl || extract.audioURL || null,
                extractName:
                  extract.extractName || `Extract ${extractIndex + 1}`,
                instructions: extract.instructions || "",
                content: {
                  questionId: question.questionId,
                  question: question.question || "",
                  options: question.options || [],
                  correctOption: question.correctOption,
                  correctAnswer: question.correctAnswer,
                },
              });
            });
          }
        });
      }

      if (partType === "C" && part.extracts) {
        part.extracts.forEach((extract, extractIndex) => {
          if (extract.questions && extract.questions.length > 0) {
            // Sort questions by order or questionId
            const sortedQuestions = [...extract.questions].sort((a, b) => {
              if (a.order != null && b.order != null) {
                return a.order - b.order;
              }
              if (a.questionId != null && b.questionId != null) {
                return a.questionId - b.questionId;
              }
              return 0;
            });

            const contents = sortedQuestions.map((question) => ({
              questionId: question.questionId,
              question: question.question || "",
              options: question.options || [],
              correctOption: question.correctOption,
              correctAnswer: question.correctAnswer,
            }));

            const questionIds = contents
              .map((c) => c?.questionId)
              .filter((v) => v != null)
              .map(Number);

            playlist.push({
              part: "C",
              type: "mcqGroup",
              audioURL: extract.audioUrl || extract.audioURL || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              contents,
              questionIds,
            });
          }
        });
      }
    });

    return playlist;
  }

  // Handle listeningParts format
  if (
    listeningData?.listeningParts &&
    Array.isArray(listeningData.listeningParts)
  ) {
    listeningData.listeningParts.forEach((part) => {
      const partType = part.part?.toUpperCase();

      if (partType === "A" && part.listeningExtract) {
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            // Sort questions by order or questionId
            const sortedQuestions = [...extract.extractQuestion].sort(
              (a, b) => {
                if (a.order != null && b.order != null) {
                  return a.order - b.order;
                }
                if (a.questionId != null && b.questionId != null) {
                  return a.questionId - b.questionId;
                }
                return 0;
              },
            );

            const byHeading = new Map();
            const questionIds = [];

            sortedQuestions.forEach((question) => {
              const heading = normalizeHeading(question.heading);
              if (!byHeading.has(heading)) byHeading.set(heading, []);

              const qid = question.questionId;
              const rawTxt = question.question || "";
              const txt = cleanQuestionText(rawTxt);
              const inferBlank =
                qid != null || /\(\d+\)/.test(rawTxt) || /_{2,}/.test(rawTxt);

              byHeading.get(heading).push({
                questionId: qid,
                text: txt,
                hasBlank: inferBlank,
                correctAnswer: question.correctAnswer,
              });

              if (qid != null) {
                questionIds.push(Number(qid));
              }
            });

            const sections = Array.from(byHeading.entries()).map(
              ([heading, notes]) => ({
                heading,
                notes: notes.sort((a, b) => {
                  if (a.questionId != null && b.questionId != null) {
                    return a.questionId - b.questionId;
                  }
                  return 0;
                }),
              }),
            );

            let patientName = "";
            const patientSec = sections.find((s) =>
              /^patient\b/i.test(s.heading),
            );
            if (patientSec?.notes?.length) {
              const nameNote =
                patientSec.notes.find((n) => !n.hasBlank && n.text?.trim()) ||
                patientSec.notes[0];
              patientName = (nameNote?.text || "").trim();
            }

            playlist.push({
              part: "A",
              type: "noteCompletion",
              audioURL: extract.audioUrl || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              content: { sections },
              questionIds,
              patientName,
            });
          }
        });
      }

      if (partType === "B" && part.listeningExtract) {
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            // Sort questions by order or questionId
            const sortedQuestions = [...extract.extractQuestion].sort(
              (a, b) => {
                if (a.order != null && b.order != null) {
                  return a.order - b.order;
                }
                if (a.questionId != null && b.questionId != null) {
                  return a.questionId - b.questionId;
                }
                return 0;
              },
            );

            sortedQuestions.forEach((question) => {
              playlist.push({
                part: "B",
                type: "mcqSingle",
                audioURL: extract.audioUrl || null,
                extractName:
                  extract.extractName || `Extract ${extractIndex + 1}`,
                instructions: extract.instructions || "",
                content: {
                  questionId: question.questionId,
                  question: question.question || "",
                  options: question.options || [],
                  correctOption: question.correctOption,
                  correctAnswer: question.correctAnswer,
                },
              });
            });
          }
        });
      }

      if (partType === "C" && part.listeningExtract) {
        part.listeningExtract.forEach((extract, extractIndex) => {
          if (extract.extractQuestion && extract.extractQuestion.length > 0) {
            // Sort questions by order or questionId
            const sortedQuestions = [...extract.extractQuestion].sort(
              (a, b) => {
                if (a.order != null && b.order != null) {
                  return a.order - b.order;
                }
                if (a.questionId != null && b.questionId != null) {
                  return a.questionId - b.questionId;
                }
                return 0;
              },
            );

            const contents = sortedQuestions.map((question) => ({
              questionId: question.questionId,
              question: question.question || "",
              options: question.options || [],
              correctOption: question.correctOption,
              correctAnswer: question.correctAnswer,
            }));

            const questionIds = contents
              .map((c) => c?.questionId)
              .filter((v) => v != null)
              .map(Number);

            playlist.push({
              part: "C",
              type: "mcqGroup",
              audioURL: extract.audioUrl || null,
              extractName: extract.extractName || `Extract ${extractIndex + 1}`,
              instructions: extract.instructions || "",
              contents,
              questionIds,
            });
          }
        });
      }
    });
  }

  return playlist;
}

/* ---------------------------------------------------------------
   Audio Player Component (Read-only Review)
--------------------------------------------------------------- */
const AudioPlayer = ({ audioUrl }) => {
  if (!audioUrl) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 1.5,
        bgcolor: "#f5f5f5",
        borderRadius: 1.5,
        border: "1px solid #e0e0e0",
      }}
    >
      <VolumeUp sx={{ color: BRAND_PRIMARY, fontSize: 24 }} />
      <audio
        controls
        src={audioUrl}
        style={{
          width: "100%",
          height: 36,
          accentColor: BRAND_PRIMARY,
        }}
      />
    </Box>
  );
};

/* ---------------------------------------------------------------
   Table/Form Completion Review Component
--------------------------------------------------------------- */
const TableFormCompletionReview = ({
  questions,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  questionMainHeading,
  tableData,
}) => {
  const maroon = BRAND_PRIMARY;

  // Helper to render table if tableData exists
  const renderTable = () => {
    if (!tableData) return null;

    const columns = tableData.columns || [];
    const rows = tableData.rows || [];

    // Parse columns
    const parsedColumns = columns.map((col) => {
      try {
        const cleaned = col.replace(/^\[?\n?"|"\n?\]?$/g, "").trim();
        return cleaned;
      } catch (e) {
        return col;
      }
    });

    // Create questions map
    const questionsMap = {};
    questions.forEach((q) => {
      if (q.questionId) {
        questionsMap[q.questionId] = {
          question: q.question || "",
          answerData: answers.find((a) => a.questionId === q.questionId),
        };
      }
    });

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `2px solid ${maroon}`,
          borderRadius: 2,
          overflow: "hidden",
          mt: 3,
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F5F5F5" }}>
              {parsedColumns.map((col, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    fontWeight: 700,
                    color: maroon,
                    fontSize: "1rem",
                    borderRight:
                      idx < parsedColumns.length - 1
                        ? `1px solid #e0e0e0`
                        : "none",
                    py: 2,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIdx) => (
              <TableRow
                key={rowIdx}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { bgcolor: "#FAFAFA" },
                }}
              >
                {row.map((cell, cellIdx) => {
                  const cellStr = String(cell).trim();
                  const questionId = parseInt(cellStr);

                  if (!isNaN(questionId) && questionsMap[questionId]) {
                    const { question, answerData: qAnswerData } =
                      questionsMap[questionId];
                    const isCorrect = qAnswerData?.isCorrect;
                    const studentAnswer =
                      qAnswerData?.studentAnswer ||
                      qAnswerData?.givenAnswer ||
                      "—";
                    const correctAnswer = qAnswerData?.correctAnswer || "—";

                    return (
                      <TableCell
                        key={cellIdx}
                        ref={(el) => {
                          if (questionRefs)
                            questionRefs.current[questionId] = el;
                        }}
                        data-question-id={questionId}
                        sx={{
                          borderRight:
                            cellIdx < row.length - 1
                              ? `1px solid #e0e0e0`
                              : "none",
                          py: 2,
                          px: 2,
                          verticalAlign: "top",
                          bgcolor: isCorrect ? SUCCESS_BG : "#fff",
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Chip
                              label={questionId}
                              size="small"
                              sx={{
                                bgcolor: maroon,
                                color: "#fff",
                                fontWeight: 700,
                                minWidth: 32,
                              }}
                            />
                            <Typography sx={{ fontSize: "0.95rem", flex: 1 }}>
                              {question.replace(/_+/g, "").trim()}
                            </Typography>
                            {isCorrect ? (
                              <CheckCircle
                                sx={{ color: SUCCESS_GREEN, fontSize: 20 }}
                              />
                            ) : (
                              <Cancel
                                sx={{ color: BRAND_PRIMARY, fontSize: 20 }}
                              />
                            )}
                          </Box>

                          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#666",
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                Your Answer:
                              </Typography>
                              <Chip
                                label={studentAnswer}
                                size="small"
                                sx={{
                                  bgcolor: isCorrect ? SUCCESS_BG : BRAND_BG,
                                  color: isCorrect
                                    ? SUCCESS_GREEN
                                    : BRAND_PRIMARY,
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                            {!isCorrect && (
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666",
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Correct Answer:
                                </Typography>
                                <Chip
                                  label={correctAnswer}
                                  size="small"
                                  sx={{
                                    bgcolor: SUCCESS_BG,
                                    color: SUCCESS_GREEN,
                                    fontWeight: 600,
                                  }}
                                />
                              </Box>
                            )}
                          </Box>

                          {onExplainRequest && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AutoAwesome />}
                              onClick={() => onExplainRequest(questionId, question.text || '', studentAnswer, correctAnswer)}
                              disabled={loadingExplanations[questionId]}
                              sx={{
                                textTransform: "none",
                                borderColor: maroon,
                                color: maroon,
                                fontSize: "0.75rem",
                                py: 0.5,
                                "&:hover": {
                                  borderColor: BRAND_DARK,
                                  bgcolor: BRAND_BG,
                                },
                              }}
                            >
                              {loadingExplanations[questionId]
                                ? "Explaining..."
                                : "Explain"}
                            </Button>
                          )}

                          {explanations[questionId] && (
                            <Paper
                              elevation={0}
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: "#f9f9f9",
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: BRAND_PRIMARY, mb: 0.5, fontSize: "0.75rem" }}
                              >
                                Explanation
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#333", lineHeight: 1.6 }}
                              >
                                {explanations[questionId]}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={cellIdx}
                      sx={{
                        borderRight:
                          cellIdx < row.length - 1
                            ? `1px solid #e0e0e0`
                            : "none",
                        py: 2,
                        fontSize: "0.95rem",
                        verticalAlign: "middle",
                      }}
                    >
                      {cellStr || "\u00A0"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render form layout for form_completion
  const renderForm = () => {
    return questions.map((question) => {
      const qid = question.questionId;
      if (!qid) return null;

      const qAnswerData = answers.find((a) => a.questionId === qid);
      const isCorrect = qAnswerData?.isCorrect;
      const studentAnswer =
        qAnswerData?.studentAnswer || qAnswerData?.givenAnswer || "—";
      const correctAnswer = qAnswerData?.correctAnswer || "—";

      return (
        <Card
          key={qid}
          elevation={0}
          ref={(el) => {
            if (questionRefs) questionRefs.current[qid] = el;
          }}
          data-question-id={qid}
          sx={{
            p: 2,
            mb: 2,
            border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Chip
              label={qid}
              size="small"
              sx={{
                bgcolor: BRAND_PRIMARY,
                color: "#fff",
                fontWeight: 700,
                minWidth: 32,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, color: "#666" }}>
                {question.questionHeading}
              </Typography>
              <Typography sx={{ fontSize: "0.95rem", mb: 2 }}>
                {question.question}
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#666", display: "block", mb: 0.5 }}
                  >
                    Your Answer:
                  </Typography>
                  <Chip
                    label={studentAnswer}
                    size="small"
                    sx={{
                      bgcolor: isCorrect ? SUCCESS_BG : BRAND_BG,
                      color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                {!isCorrect && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block", mb: 0.5 }}
                    >
                      Correct Answer:
                    </Typography>
                    <Chip
                      label={correctAnswer}
                      size="small"
                      sx={{
                        bgcolor: SUCCESS_BG,
                        color: SUCCESS_GREEN,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                )}
              </Box>

              {onExplainRequest && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutoAwesome />}
                  onClick={() => onExplainRequest(qid, question.question || '', studentAnswer, correctAnswer)}
                  disabled={loadingExplanations[qid]}
                  sx={{
                    textTransform: "none",
                    borderColor: maroon,
                    color: maroon,
                    "&:hover": {
                      borderColor: BRAND_DARK,
                      bgcolor: BRAND_BG,
                    },
                  }}
                >
                  {loadingExplanations[qid] ? "Explaining..." : "Explain"}
                </Button>
              )}

              <ExplanationBlock text={explanations[qid]} />
            </Box>
            {isCorrect ? (
              <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 24 }} />
            ) : (
              <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 24 }} />
            )}
          </Box>
        </Card>
      );
    });
  };

  return (
    <Box sx={{ mb: 3 }}>
      {questionMainHeading && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2.5,
            border: `2px solid ${maroon}`,
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: maroon,
              textAlign: "center",
            }}
          >
            {questionMainHeading}
          </Typography>
        </Paper>
      )}

      {tableData ? renderTable() : renderForm()}
    </Box>
  );
};

/* ---------------------------------------------------------------
   Note Completion Review (Part A - Matching Original Layout)
--------------------------------------------------------------- */
const NoteCompletionReview = ({
  sections,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
}) => {
  // Get answer for a question
  const getAnswerData = (questionId) => {
    return answers?.find((a) => Number(a.questionId) === Number(questionId));
  };

  // Filter out the Patient section for display
  const filteredSections = sections.filter(
    (s) => !/^patient$/i.test(s.heading?.trim()),
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {filteredSections.map((section, sIdx) => (
        <Card
          key={sIdx}
          elevation={0}
          sx={{
            p: 0,
            border: "1px solid #e0e0e0",
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          {/* Section Heading */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              bgcolor: "#fafafa",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <HtmlRenderer
              content={decodeHtmlEntities(section.heading)}
              sx={{
                fontWeight: 700,
                color: BRAND_PRIMARY,
                fontSize: "0.95rem",
              }}
            />
          </Box>

          {/* Notes in this section */}
          <Box sx={{ p: 2.5 }}>
            {section.notes?.map((note, nIdx) => {
              const qid = note?.questionId;
              const hasQuestion = Number.isInteger(qid) && qid > 0;

              // For non-question notes, just display the text
              if (!hasQuestion) {
                return (
                  <Box key={nIdx} sx={{ mb: 1 }}>
                    <HtmlRenderer
                      content={note.text}
                      sx={{
                        fontSize: "0.875rem",
                        color: "#333",
                        lineHeight: 1.6,
                      }}
                    />
                  </Box>
                );
              }

              // For question notes, display with answer info
              const answerData = getAnswerData(qid);
              const isCorrect = answerData?.isCorrect;
              const studentAnswer =
                answerData?.studentAnswer || answerData?.givenAnswer || "—";
              const correctAnswer =
                answerData?.correctAnswer || note.correctAnswer || "—";

              return (
                <Box
                  key={nIdx}
                  ref={(el) => {
                    if (questionRefs) questionRefs.current[qid] = el;
                  }}
                  data-question-id={qid}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: "#fff",
                    border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
                  }}
                >
                  {/* Question text with number */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Chip
                      label={qid}
                      size="small"
                      sx={{
                        bgcolor: BRAND_PRIMARY,
                        color: "#fff",
                        fontWeight: 700,
                        minWidth: 28,
                        height: 24,
                        fontSize: "0.75rem",
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <HtmlRenderer
                        content={note.text}
                        sx={{
                          fontSize: "0.875rem",
                          color: "#333",
                          lineHeight: 1.6,
                        }}
                      />
                    </Box>
                    {isCorrect ? (
                      <CheckCircle
                        sx={{ color: SUCCESS_GREEN, fontSize: 22 }}
                      />
                    ) : (
                      <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 22 }} />
                    )}
                  </Box>

                  {/* Answer display */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 3,
                      pl: 5,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          fontWeight: 600,
                          display: "block",
                        }}
                      >
                        Your Answer:
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                          fontSize: "0.875rem",
                        }}
                      >
                        {studentAnswer}
                      </Typography>
                    </Box>
                    {!isCorrect && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#666",
                            fontWeight: 600,
                            display: "block",
                          }}
                        >
                          Correct Answer:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: SUCCESS_GREEN,
                            fontSize: "0.875rem",
                          }}
                        >
                          {correctAnswer}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* AI Explain Button */}
                  {onExplainRequest && !isCorrect && (
                    <Box sx={{ mt: 1.5, pl: 5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          loadingExplanations?.[qid] ? (
                            <CircularProgress size={14} />
                          ) : (
                            <AutoAwesome sx={{ fontSize: 16 }} />
                          )
                        }
                        onClick={() =>
                          onExplainRequest(
                            qid,
                            note.text,
                            studentAnswer,
                            correctAnswer,
                          )
                        }
                        disabled={loadingExplanations?.[qid]}
                        sx={{
                          borderColor: BRAND_PRIMARY,
                          color: BRAND_PRIMARY,
                          fontSize: "0.75rem",
                          py: 0.5,
                          "&:hover": {
                            borderColor: BRAND_DARK,
                            bgcolor: BRAND_BG,
                          },
                        }}
                      >
                        Explain
                      </Button>
                      <ExplanationBlock text={explanations[qid]} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Card>
      ))}
    </Box>
  );
};

/* ---------------------------------------------------------------
   MCQ Review Component (Part B & C - Matching Original Layout)
--------------------------------------------------------------- */
const MCQReview = ({
  mcqData,
  answers,
  questionIds, // For MCQ multiple: array of question IDs to fetch all answers
  isMultiple = false, // Flag to indicate MCQ multiple (choose TWO)
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const {
    questionId,
    question,
    options = [],
    correctOption,
    correctAnswer,
  } = mcqData;

  // For multiple MCQ, create display label as range (e.g., "14-15")
  const displayQuestionId =
    isMultiple && questionIds && questionIds.length > 1
      ? `${questionIds[0]}-${questionIds[questionIds.length - 1]}`
      : questionId;

  // For MCQ multiple, get answers from all question IDs
  let answerData;
  let allStudentAnswers = "";

  if (isMultiple && questionIds && questionIds.length > 0) {
    // MCQ Multiple: Use only the first question's answer (they're duplicates in the backend)
    answerData = answers?.find(
      (a) => Number(a.questionId) === Number(questionIds[0]),
    );
    allStudentAnswers =
      answerData?.studentAnswer || answerData?.givenAnswer || "";
  } else {
    // Single MCQ: Find answer for this question only
    answerData = answers?.find(
      (a) => Number(a.questionId) === Number(questionId),
    );
    allStudentAnswers =
      answerData?.studentAnswer || answerData?.givenAnswer || "";
  }

  // Parse answers for multiple selection support
  const parseAnswers = (answerString) => {
    if (!answerString) return [];
    if (typeof answerString === "string") {
      return answerString
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    }
    if (Array.isArray(answerString)) {
      return answerString.map((s) => String(s).trim().toLowerCase());
    }
    return [String(answerString).trim().toLowerCase()];
  };

  const studentAnswersLower = parseAnswers(allStudentAnswers);

  // For correct answers, use only from the first answer data (avoid duplicates)
  const correctAnswerText =
    answerData?.correctOption ||
    answerData?.correctAnswer ||
    correctOption ||
    correctAnswer ||
    "";
  const correctAnswersLower = parseAnswers(correctAnswerText);

  const isCorrect = answerData?.isCorrect;

  // Helper to get option letter
  const letterOfIdx = (i) => String.fromCharCode(65 + i);
  const optionText = (opt) => opt?.text ?? opt?.value ?? opt?.label ?? opt;
  const optionLabel = (opt) => opt?.label ?? "";

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) {
          // Register the element for the main questionId
          questionRefs.current[questionId] = el;
          // For multiple MCQ, also register all other question IDs in the group
          if (isMultiple && questionIds && questionIds.length > 1) {
            questionIds.forEach((qid) => {
              questionRefs.current[qid] = el;
            });
          }
        }
      }}
      data-question-id={questionId}
      sx={{
        p: 0,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Chip
          label={displayQuestionId}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 28,
            height: 24,
            fontSize: "0.75rem",
          }}
        />
        <Box sx={{ flex: 1 }}>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <HtmlRenderer
              content={question}
              sx={{
                fontWeight: 500,
                fontSize: "0.9rem",
                lineHeight: 1.5,
                color: "#333",
              }}
            />
          </TextHighlighterWrapper>
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 24 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 24 }} />
        )}
      </Box>

      {/* Options */}
      <Box sx={{ p: 2.5, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          {options.map((opt, idx) => {
            const optLetter = (
              optionLabel(opt) || letterOfIdx(idx)
            ).toLowerCase();

            const isStudentChoice = studentAnswersLower.includes(optLetter);
            const isCorrectChoice = correctAnswersLower.includes(optLetter);

            let bgColor = "#fff";
            let borderColor = "#e0e0e0";
            let leftBorderColor = "transparent";

            if (isCorrectChoice) {
              bgColor = SUCCESS_BG;
              borderColor = SUCCESS_GREEN;
              leftBorderColor = SUCCESS_GREEN;
            } else if (isStudentChoice && !isCorrectChoice) {
              bgColor = BRAND_BG;
              borderColor = BRAND_PRIMARY;
              leftBorderColor = BRAND_PRIMARY;
            }

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: bgColor,
                  border: `1px solid ${borderColor}`,
                  borderLeft: `4px solid ${leftBorderColor}`,
                }}
              >
                {/* Option Letter Circle */}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    minWidth: 28,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: isCorrectChoice || isStudentChoice ? "#fff" : "#555",
                    bgcolor: isCorrectChoice
                      ? SUCCESS_GREEN
                      : isStudentChoice
                        ? BRAND_PRIMARY
                        : "#e0e0e0",
                  }}
                >
                  {(optionLabel(opt) || letterOfIdx(idx)).toUpperCase()}
                </Box>

                {/* Option Text */}
                <Box sx={{ flex: 1 }}>
                  <HtmlRenderer
                    content={optionText(opt)}
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                      color: "#333",
                    }}
                  />
                </Box>

                {/* Status Icon */}
                {isCorrectChoice && (
                  <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />
                )}
                {isStudentChoice && !isCorrectChoice && (
                  <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                )}

                {/* Show badge for student's selected answers */}
                {isStudentChoice && (
                  <Box
                    sx={{
                      fontSize: "11px",
                      padding: "3px 10px",
                      borderRadius: "6px",
                      backgroundColor: isCorrectChoice
                        ? SUCCESS_GREEN
                        : BRAND_PRIMARY,
                      color: "white",
                      fontWeight: 500,
                    }}
                  >
                    Your Answer
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Summary Row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            mt: 2,
            pt: 2,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Your Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                textTransform: "uppercase",
                fontSize: "0.875rem",
              }}
            >
              {studentAnswersLower.join(", ").toUpperCase() || "Not answered"}
            </Typography>
          </Box>
          {!isCorrect && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#666", fontWeight: 600 }}
              >
                Correct Answer:
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: SUCCESS_GREEN,
                  textTransform: "uppercase",
                  fontSize: "0.875rem",
                }}
              >
                {correctAnswersLower.join(", ").toUpperCase() || "—"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* AI Explain Button */}
        {onExplainRequest && !isCorrect && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                loadingExplanations?.[questionId] ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesome sx={{ fontSize: 16 }} />
                )
              }
              onClick={() =>
                onExplainRequest(
                  questionId,
                  question,
                  studentAnswersLower.join(", "),
                  correctAnswersLower.join(", "),
                )
              }
              disabled={loadingExplanations?.[questionId]}
              sx={{
                borderColor: BRAND_PRIMARY,
                color: BRAND_PRIMARY,
                fontSize: "0.75rem",
                py: 0.5,
                "&:hover": {
                  borderColor: BRAND_DARK,
                  bgcolor: BRAND_BG,
                },
              }}
            >
              Explain
            </Button>
            {explanations?.[questionId] && (
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: BRAND_BG,
                  borderLeft: `4px solid ${BRAND_PRIMARY}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: BRAND_PRIMARY, mb: 0.5, fontSize: "0.75rem" }}
                >
                  Explanation
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#333", lineHeight: 1.6 }}
                >
                  {explanations[questionId]}
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

/* ---------------------------------------------------------------
   Map Labelling Review Component (Dropdown-based)
--------------------------------------------------------------- */
const MapLabellingReview = ({
  mapData,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const { questionId, question, options = [], correctOption } = mapData;

  // Find answer for this question
  const answerData = answers?.find(
    (a) => Number(a.questionId) === Number(questionId),
  );

  const studentAnswer =
    answerData?.studentAnswer || answerData?.givenAnswer || "";
  const isCorrect = answerData?.isCorrect;

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[questionId] = el;
      }}
      data-question-id={questionId}
      sx={{
        p: 0,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Chip
          label={questionId}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 28,
            height: 24,
            fontSize: "0.75rem",
          }}
        />
        <Box sx={{ flex: 1 }}>
          <TextHighlighterWrapper onAddNote={onAddNote}>
            <HtmlRenderer
              content={question}
              sx={{
                fontWeight: 500,
                fontSize: "0.9rem",
                lineHeight: 1.5,
                color: "#333",
              }}
            />
          </TextHighlighterWrapper>
        </Box>
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 24 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 24 }} />
        )}
      </Box>

      {/* Dropdown Options Display */}
      <Box sx={{ p: 2.5, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          {options.map((opt, idx) => {
            const optValue = (opt?.text || opt?.label || opt).trim();
            const isStudentChoice =
              studentAnswer?.trim().toUpperCase() === optValue.toUpperCase();
            const isCorrectChoice =
              correctOption?.trim().toUpperCase() === optValue.toUpperCase();

            let bgColor = "#fff";
            let borderColor = "#e0e0e0";
            let leftBorderColor = "transparent";

            if (isCorrectChoice) {
              bgColor = SUCCESS_BG;
              borderColor = SUCCESS_GREEN;
              leftBorderColor = SUCCESS_GREEN;
            } else if (isStudentChoice && !isCorrectChoice) {
              bgColor = BRAND_BG;
              borderColor = BRAND_PRIMARY;
              leftBorderColor = BRAND_PRIMARY;
            }

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: bgColor,
                  border: `1px solid ${borderColor}`,
                  borderLeft: `4px solid ${leftBorderColor}`,
                }}
              >
                {/* Option Text */}
                <Box sx={{ flex: 1 }}>
                  <HtmlRenderer
                    content={optValue}
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                      color: "#333",
                      fontWeight:
                        isCorrectChoice || isStudentChoice ? 600 : 400,
                    }}
                  />
                </Box>

                {/* Status Icon */}
                {isCorrectChoice && (
                  <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />
                )}
                {isStudentChoice && !isCorrectChoice && (
                  <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                )}
              </Box>
            );
          })}
        </Box>

        {/* Summary Row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            mt: 2,
            pt: 2,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Your Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                fontSize: "0.875rem",
              }}
            >
              {studentAnswer || "Not answered"}
            </Typography>
          </Box>
          {!isCorrect && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#666", fontWeight: 600 }}
              >
                Correct Answer:
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: SUCCESS_GREEN,
                  fontSize: "0.875rem",
                }}
              >
                {correctOption || "—"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* AI Explain Button */}
        {onExplainRequest && !isCorrect && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                loadingExplanations?.[questionId] ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesome sx={{ fontSize: 16 }} />
                )
              }
              onClick={() =>
                onExplainRequest(
                  questionId,
                  question,
                  studentAnswer,
                  correctOption,
                )
              }
              disabled={loadingExplanations?.[questionId]}
              sx={{
                borderColor: BRAND_PRIMARY,
                color: BRAND_PRIMARY,
                fontSize: "0.75rem",
                py: 0.5,
                "&:hover": {
                  borderColor: BRAND_DARK,
                  bgcolor: BRAND_BG,
                },
              }}
            >
              Explain
            </Button>
            {explanations?.[questionId] && (
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: BRAND_BG,
                  borderLeft: `4px solid ${BRAND_PRIMARY}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: BRAND_PRIMARY, mb: 0.5, fontSize: "0.75rem" }}
                >
                  Explanation
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#333", lineHeight: 1.6 }}
                >
                  {explanations[questionId]}
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

/* ---------------------------------------------------------------
   Matching Information Review Component (Dropdown with multi-line layout)
--------------------------------------------------------------- */
const MatchingInformationReview = ({
  matchingData,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const { questionId, question, options = [], correctOption } = matchingData;

  // Find answer for this question
  const answerData = answers?.find(
    (a) => Number(a.questionId) === Number(questionId),
  );

  const studentAnswer =
    answerData?.studentAnswer || answerData?.givenAnswer || "";
  const isCorrect = answerData?.isCorrect;

  return (
    <Card
      elevation={0}
      ref={(el) => {
        if (questionRefs) questionRefs.current[questionId] = el;
      }}
      data-question-id={questionId}
      sx={{
        p: 0,
        border: `1px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "#fff",
        mb: 2,
        width: "100%",
      }}
    >
      {/* Question Header with ID and Status Icon */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Chip
          label={questionId}
          size="small"
          sx={{
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            fontWeight: 700,
            minWidth: 28,
            height: 24,
            fontSize: "0.75rem",
          }}
        />
        {isCorrect ? (
          <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 24 }} />
        ) : (
          <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 24 }} />
        )}
      </Box>

      {/* Question Text on Separate Line */}
      <Box sx={{ px: 2.5, pt: 2 }}>
        <TextHighlighterWrapper onAddNote={onAddNote}>
          <HtmlRenderer
            content={question}
            sx={{
              fontWeight: 500,
              fontSize: "0.9rem",
              lineHeight: 1.6,
              color: "#333",
              mb: 2,
            }}
          />
        </TextHighlighterWrapper>
      </Box>

      {/* Dropdown Options Display */}
      <Box sx={{ px: 2.5, pb: 2.5, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          {options.map((opt, idx) => {
            const optValue = (opt?.text || opt?.label || opt).trim();
            const isStudentChoice =
              studentAnswer?.trim().toUpperCase() === optValue.toUpperCase();
            const isCorrectChoice =
              correctOption?.trim().toUpperCase() === optValue.toUpperCase();

            let bgColor = "#fff";
            let borderColor = "#e0e0e0";
            let leftBorderColor = "transparent";

            if (isCorrectChoice) {
              bgColor = SUCCESS_BG;
              borderColor = SUCCESS_GREEN;
              leftBorderColor = SUCCESS_GREEN;
            } else if (isStudentChoice && !isCorrectChoice) {
              bgColor = BRAND_BG;
              borderColor = BRAND_PRIMARY;
              leftBorderColor = BRAND_PRIMARY;
            }

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: bgColor,
                  border: `1px solid ${borderColor}`,
                  borderLeft: `4px solid ${leftBorderColor}`,
                }}
              >
                {/* Option Text */}
                <Box sx={{ flex: 1 }}>
                  <HtmlRenderer
                    content={optValue}
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                      color: "#333",
                      fontWeight:
                        isCorrectChoice || isStudentChoice ? 600 : 400,
                    }}
                  />
                </Box>

                {/* Status Icon */}
                {isCorrectChoice && (
                  <CheckCircle sx={{ color: SUCCESS_GREEN, fontSize: 20 }} />
                )}
                {isStudentChoice && !isCorrectChoice && (
                  <Cancel sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                )}
              </Box>
            );
          })}
        </Box>

        {/* Summary Row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            mt: 2,
            pt: 2,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontWeight: 600 }}
            >
              Your Answer:
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                fontSize: "0.875rem",
              }}
            >
              {studentAnswer || "Not answered"}
            </Typography>
          </Box>
          {!isCorrect && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#666", fontWeight: 600 }}
              >
                Correct Answer:
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: SUCCESS_GREEN,
                  fontSize: "0.875rem",
                }}
              >
                {correctOption || "—"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* AI Explain Button */}
        {onExplainRequest && !isCorrect && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                loadingExplanations?.[questionId] ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesome sx={{ fontSize: 16 }} />
                )
              }
              onClick={() =>
                onExplainRequest(
                  questionId,
                  question,
                  studentAnswer,
                  correctOption,
                )
              }
              disabled={loadingExplanations?.[questionId]}
              sx={{
                borderColor: BRAND_PRIMARY,
                color: BRAND_PRIMARY,
                fontSize: "0.75rem",
                py: 0.5,
                "&:hover": {
                  borderColor: BRAND_DARK,
                  bgcolor: BRAND_BG,
                },
              }}
            >
              Explain
            </Button>
            {explanations?.[questionId] && (
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: BRAND_BG,
                  borderLeft: `4px solid ${BRAND_PRIMARY}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: BRAND_PRIMARY, mb: 0.5, fontSize: "0.75rem" }}
                >
                  Explanation
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#333", lineHeight: 1.6 }}
                >
                  {explanations[questionId]}
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

/* ---------------------------------------------------------------
   Section Review Component - Renders each section separately
--------------------------------------------------------------- */
const SectionReview = ({
  sectionTitle,
  sectionOrder,
  audioURL,
  screens,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  expanded = true,
  onToggleExpand,
  onAddNote,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Section Header */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: BRAND_PRIMARY,
          borderRadius: 1.5,
          cursor: "pointer",
        }}
        onClick={onToggleExpand}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              {sectionTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Review all questions in this section
            </Typography>
          </Box>
          <IconButton sx={{ color: "#fff" }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Card>

      {/* Section Content */}
      <Collapse in={expanded}>
        <Card
          elevation={0}
          sx={{
            mb: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          {/* Audio Player */}
          {audioURL && (
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
              <AudioPlayer audioUrl={audioURL} />
            </Box>
          )}

          {/* Render questions based on type */}
          {screens.map((screen, screenIdx) => {
            // Handle table_completion, form_completion, and flowchart_completion with new component
            if (
              screen.type === "tableCompletion" ||
              screen.type === "formCompletion" ||
              screen.type === "flowchartCompletion"
            ) {
              // Check if this is the first in a group with same type and instructions
              const prevScreen = screenIdx > 0 ? screens[screenIdx - 1] : null;
              const isFirstInGroup =
                !prevScreen ||
                prevScreen.type !== screen.type ||
                prevScreen.instructions !== screen.instructions ||
                (screen.type === "flowchartCompletion" &&
                  prevScreen.features !== screen.features);

              return (
                <Box key={screenIdx}>
                  {/* Instructions and Features (show once for first question in group) */}
                  {isFirstInGroup &&
                    (screen.instructions || screen.features) && (
                      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                        {screen.instructions && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1.5,
                              bgcolor: "#fafafa",
                              mb:
                                screen.features &&
                                screen.type === "flowchartCompletion"
                                  ? 2
                                  : 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <InfoOutlined
                                sx={{
                                  fontSize: 22,
                                  color: BRAND_PRIMARY,
                                  mr: 1,
                                }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                              >
                                Instruction
                              </Typography>
                            </Box>
                            <HtmlRenderer
                              content={screen.instructions}
                              sx={{
                                fontSize: "0.875rem",
                                color: "#555",
                                lineHeight: 1.6,
                              }}
                            />
                          </Paper>
                        )}
                        {screen.features &&
                          screen.type === "flowchartCompletion" && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1.5,
                                bgcolor: "#fff3e0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <InfoOutlined
                                  sx={{ fontSize: 22, color: "#f57c00", mr: 1 }}
                                />
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 700, color: "#f57c00" }}
                                >
                                  Options
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.875rem",
                                  color: "#555",
                                  lineHeight: 1.8,
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "monospace",
                                }}
                              >
                                {screen.features}
                              </Typography>
                            </Paper>
                          )}
                      </Box>
                    )}

                  {/* Table/Form Completion Questions */}
                  <Box sx={{ p: 2 }}>
                    <TableFormCompletionReview
                      questions={screen.questions || []}
                      answers={answers}
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                      questionMainHeading={screen.questionMainHeading || ""}
                      tableData={screen.tableData || null}
                    />
                  </Box>
                </Box>
              );
            }

            if (screen.type === "noteCompletion") {
              return (
                <Box key={screenIdx}>
                  {/* Instructions */}
                  {screen.instructions && (
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1.5,
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <InfoOutlined
                            sx={{ fontSize: 22, color: BRAND_PRIMARY, mr: 1 }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                          >
                            Instruction
                          </Typography>
                        </Box>
                        <HtmlRenderer
                          content={screen.instructions}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#555",
                            lineHeight: 1.6,
                          }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* Note Completion Questions */}
                  <Box sx={{ p: 2 }}>
                    <NoteCompletionReview
                      sections={screen.content?.sections || []}
                      answers={answers}
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                    />
                  </Box>
                </Box>
              );
            } else if (screen.type === "mcqSingle") {
              // Check if this is the first mcqSingle question in a consecutive group with same instructions
              const prevScreen = screenIdx > 0 ? screens[screenIdx - 1] : null;
              const isFirstInGroup =
                !prevScreen ||
                prevScreen.type !== "mcqSingle" ||
                prevScreen.instructions !== screen.instructions;

              return (
                <Box key={screenIdx}>
                  {/* Instructions (show once for first question in group) */}
                  {isFirstInGroup && screen.instructions && (
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1.5,
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <InfoOutlined
                            sx={{ fontSize: 22, color: BRAND_PRIMARY, mr: 1 }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                          >
                            Instruction
                          </Typography>
                        </Box>
                        <HtmlRenderer
                          content={screen.instructions}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#555",
                            lineHeight: 1.6,
                          }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* MCQ Question */}
                  <Box
                    sx={{
                      p: 2,
                      borderTop: screenIdx > 0 ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    <MCQReview
                      mcqData={screen.content}
                      answers={answers}
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                      onAddNote={onAddNote}
                    />
                  </Box>
                </Box>
              );
            } else if (screen.type === "mcqMultiple") {
              // Handle MCQ Multiple (Choose TWO answers) - Single question card with multiple correct answers
              // Determine if this is the first in a group of consecutive mcq_multiple with same instructions
              const prevScreen = screenIdx > 0 ? screens[screenIdx - 1] : null;
              const isFirstInGroup =
                !prevScreen ||
                prevScreen.type !== "mcqMultiple" ||
                prevScreen.instructions !== screen.instructions;

              return (
                <Box key={screenIdx}>
                  {/* Instructions (show once per group of consecutive mcq_multiple with same instructions) */}
                  {isFirstInGroup && screen.instructions && (
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1.5,
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <InfoOutlined
                            sx={{ fontSize: 22, color: BRAND_PRIMARY, mr: 1 }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                          >
                            Instruction
                          </Typography>
                        </Box>
                        <HtmlRenderer
                          content={screen.instructions}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#555",
                            lineHeight: 1.6,
                          }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* MCQ Multiple Question - Pass grouped data */}
                  <Box
                    sx={{
                      p: 2,
                      borderTop: screenIdx > 0 ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    <MCQReview
                      mcqData={screen.content}
                      answers={answers}
                      questionIds={screen.questionIds} // Pass all question IDs for this group
                      isMultiple={true} // Flag to indicate this is MCQ multiple
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                      onAddNote={onAddNote}
                    />
                  </Box>
                </Box>
              );
            } else if (screen.type === "mapLabelling") {
              // Check if this is the first map_labelling question in a consecutive group with same image/instructions
              const prevScreen = screenIdx > 0 ? screens[screenIdx - 1] : null;
              const isFirstInGroup =
                !prevScreen ||
                prevScreen.type !== "mapLabelling" ||
                prevScreen.imageUrl !== screen.imageUrl ||
                prevScreen.instructions !== screen.instructions;

              return (
                <Box key={screenIdx}>
                  {/* Instructions (show once for first question in group) */}
                  {isFirstInGroup && screen.instructions && (
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1.5,
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <InfoOutlined
                            sx={{ fontSize: 22, color: BRAND_PRIMARY, mr: 1 }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                          >
                            Instruction
                          </Typography>
                        </Box>
                        <HtmlRenderer
                          content={screen.instructions}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#555",
                            lineHeight: 1.6,
                          }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* Main Heading (show once for first question in group) */}
                  {isFirstInGroup && screen.questionMainHeading && (
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                      >
                        {screen.questionMainHeading}
                      </Typography>
                    </Box>
                  )}

                  {/* Image (show once for first question in group) */}
                  {isFirstInGroup && screen.imageUrl && (
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: "1px solid #e0e0e0",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={screen.imageUrl}
                        alt="Map diagram"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: "8px",
                        }}
                      />
                    </Box>
                  )}

                  {/* Map Labelling Question */}
                  <Box
                    sx={{
                      p: 2,
                      borderTop: !isFirstInGroup ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    <MapLabellingReview
                      mapData={screen.content}
                      answers={answers}
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                      onAddNote={onAddNote}
                    />
                  </Box>
                </Box>
              );
            } else if (screen.type === "matchingInformation") {
              // Check if this is the first matching_information question in a consecutive group with same instructions and features
              const prevScreen = screenIdx > 0 ? screens[screenIdx - 1] : null;
              const isFirstInGroup =
                !prevScreen ||
                prevScreen.type !== "matchingInformation" ||
                prevScreen.instructions !== screen.instructions ||
                prevScreen.features !== screen.features;

              return (
                <Box key={screenIdx}>
                  {/* Instructions and Features (show once for first question in group) */}
                  {isFirstInGroup &&
                    (screen.instructions || screen.features) && (
                      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                        {screen.instructions && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1.5,
                              bgcolor: "#fafafa",
                              mb: screen.features ? 2 : 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <InfoOutlined
                                sx={{
                                  fontSize: 22,
                                  color: BRAND_PRIMARY,
                                  mr: 1,
                                }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                              >
                                Instruction
                              </Typography>
                            </Box>
                            <HtmlRenderer
                              content={screen.instructions}
                              sx={{
                                fontSize: "0.875rem",
                                color: "#555",
                                lineHeight: 1.6,
                              }}
                            />
                          </Paper>
                        )}
                        {screen.features && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1.5,
                              bgcolor: "#fff3e0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <InfoOutlined
                                sx={{ fontSize: 22, color: "#f57c00", mr: 1 }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: "#f57c00" }}
                              >
                                Options
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.875rem",
                                color: "#555",
                                lineHeight: 1.8,
                                whiteSpace: "pre-wrap",
                                fontFamily: "monospace",
                              }}
                            >
                              {screen.features}
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                    )}

                  {/* Main Heading if present */}
                  {screen.questionMainHeading && (
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                      >
                        {screen.questionMainHeading}
                      </Typography>
                    </Box>
                  )}

                  {/* Matching Information Question */}
                  <Box
                    sx={{
                      p: 2,
                      borderTop: screenIdx > 0 ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    <MatchingInformationReview
                      matchingData={screen.content}
                      answers={answers}
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                      onAddNote={onAddNote}
                    />
                  </Box>
                </Box>
              );
            } else if (screen.type === "matchingFeature") {
              // Check if this is the first matching_feature question in a consecutive group with same instructions and features
              const prevScreen = screenIdx > 0 ? screens[screenIdx - 1] : null;
              const isFirstInGroup =
                !prevScreen ||
                prevScreen.type !== "matchingFeature" ||
                prevScreen.instructions !== screen.instructions ||
                prevScreen.features !== screen.features;

              return (
                <Box key={screenIdx}>
                  {/* Instructions and Features (show once for first question in group) */}
                  {isFirstInGroup &&
                    (screen.instructions || screen.features) && (
                      <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                        {screen.instructions && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1.5,
                              bgcolor: "#fafafa",
                              mb: screen.features ? 2 : 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <InfoOutlined
                                sx={{
                                  fontSize: 22,
                                  color: BRAND_PRIMARY,
                                  mr: 1,
                                }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                              >
                                Instruction
                              </Typography>
                            </Box>
                            <HtmlRenderer
                              content={screen.instructions}
                              sx={{
                                fontSize: "0.875rem",
                                color: "#555",
                                lineHeight: 1.6,
                              }}
                            />
                          </Paper>
                        )}
                        {screen.features && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1.5,
                              bgcolor: "#fff3e0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <InfoOutlined
                                sx={{ fontSize: 22, color: "#f57c00", mr: 1 }}
                              />
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: "#f57c00" }}
                              >
                                Options
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.875rem",
                                color: "#555",
                                lineHeight: 1.8,
                                whiteSpace: "pre-wrap",
                                fontFamily: "monospace",
                              }}
                            >
                              {screen.features}
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                    )}

                  {/* Main Heading if present */}
                  {screen.questionMainHeading && (
                    <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                      >
                        {screen.questionMainHeading}
                      </Typography>
                    </Box>
                  )}

                  {/* Matching Feature Question */}
                  <Box
                    sx={{
                      p: 2,
                      borderTop: screenIdx > 0 ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    <MatchingInformationReview
                      matchingData={screen.content}
                      answers={answers}
                      onExplainRequest={onExplainRequest}
                      loadingExplanations={loadingExplanations}
                      explanations={explanations}
                      questionRefs={questionRefs}
                      onAddNote={onAddNote}
                    />
                  </Box>
                </Box>
              );
            }
            return null;
          })}
        </Card>
      </Collapse>
    </Box>
  );
};

/* ---------------------------------------------------------------
   Part A Review Section
--------------------------------------------------------------- */
const PartAReview = ({
  screens,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Part Header - Collapsible */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: BRAND_PRIMARY,
          borderRadius: 1.5,
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              Part A - Note Completion
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Listen and complete the notes
            </Typography>
          </Box>
          <IconButton sx={{ color: "#fff" }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Card>

      {/* Collapsible Content */}
      <Collapse in={expanded}>
        {screens.map((screen, screenIdx) => {
          // Get first non-Patient section for summary
          const filteredSections = (screen.content?.sections || []).filter(
            (s) => !/^patient$/i.test(s.heading?.trim()),
          );
          const firstSec = filteredSections[0];
          const summaryLines = firstSec
            ? (firstSec.notes || [])
                .filter((n) => n.questionId == null && n.text?.trim())
                .slice(0, 2)
                .map((n) => n.text.trim())
            : [];

          return (
            <Card
              key={screenIdx}
              elevation={0}
              sx={{
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: "#fff",
              }}
            >
              {/* Extract Header */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#fafafa",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                >
                  {screen.extractName}
                </Typography>
              </Box>

              {/* Patient Summary Card (Like Original) */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    bgcolor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    sx={{
                      bgcolor: BRAND_PRIMARY,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>

                  {/* Patient name block */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#757575",
                        lineHeight: 1.2,
                      }}
                    >
                      Patient Name
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: "#000",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {screen.patientName || "—"}
                    </Typography>
                  </Box>

                  {/* Summary */}
                  {firstSec && summaryLines.length > 0 && (
                    <Box
                      sx={{
                        pl: 3,
                        minWidth: 180,
                        maxWidth: { xs: 200, sm: 260 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 0.25,
                          color: BRAND_PRIMARY,
                          fontWeight: 700,
                          fontSize: 14,
                          lineHeight: 1.2,
                        }}
                      >
                        <ArrowRightIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        <HtmlRenderer
                          content={decodeHtmlEntities(firstSec.heading)}
                          sx={{
                            color: "inherit",
                            fontWeight: "inherit",
                            fontSize: "inherit",
                            lineHeight: "inherit",
                          }}
                        />
                      </Box>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.5,
                          color: BRAND_PRIMARY,
                          fontSize: 13,
                          lineHeight: 1.3,
                          "& li": { mb: 0.25, listStyle: "disc" },
                        }}
                      >
                        {summaryLines.map((txt, i) => (
                          <li key={i}>
                            <HtmlRenderer
                              content={decodeHtmlEntities(txt)}
                              component="span"
                              sx={{ color: "inherit", fontSize: "inherit" }}
                            />
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Card>
              </Box>

              {/* Audio Player */}
              {screen.audioURL && (
                <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                  <AudioPlayer audioUrl={screen.audioURL} />
                </Box>
              )}

              {/* Note Completion Content */}
              <Box sx={{ p: 2 }}>
                <NoteCompletionReview
                  sections={screen.content?.sections || []}
                  answers={answers}
                  onExplainRequest={onExplainRequest}
                  loadingExplanations={loadingExplanations}
                  explanations={explanations}
                  questionRefs={questionRefs}
                />
              </Box>
            </Card>
          );
        })}
      </Collapse>
    </Box>
  );
};

/* ---------------------------------------------------------------
   Part B Review Section
--------------------------------------------------------------- */
const PartBReview = ({
  screens,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const [expanded, setExpanded] = useState(true);

  // Group screens by extract
  const groupedScreens = useMemo(() => {
    const groups = new Map();
    screens.forEach((screen) => {
      const key = screen.extractName;
      if (!groups.has(key)) {
        groups.set(key, {
          extractName: screen.extractName,
          audioURL: screen.audioURL,
          instructions: screen.instructions,
          questions: [],
        });
      }
      groups.get(key).questions.push(screen.content);
    });
    return Array.from(groups.values());
  }, [screens]);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Part Header */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: BRAND_PRIMARY,
          borderRadius: 1.5,
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              Part B - Multiple Choice
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Select the best answer
            </Typography>
          </Box>
          <IconButton sx={{ color: "#fff" }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Card>

      {/* Content */}
      <Collapse in={expanded}>
        {groupedScreens.map((group, groupIdx) => (
          <Card
            key={groupIdx}
            elevation={0}
            sx={{
              mb: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1.5,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            {/* Extract Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
              >
                {group.extractName}
              </Typography>
            </Box>

            {/* Audio Player */}
            {group.audioURL && (
              <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <AudioPlayer audioUrl={group.audioURL} />
              </Box>
            )}

            {/* Instructions */}
            {group.instructions && (
              <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1.5,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <InfoOutlined
                      sx={{ fontSize: 22, color: BRAND_PRIMARY, mr: 1 }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                    >
                      Instruction
                    </Typography>
                  </Box>
                  <HtmlRenderer
                    content={group.instructions}
                    sx={{
                      fontSize: "0.875rem",
                      color: "#555",
                      lineHeight: 1.6,
                    }}
                  />
                </Paper>
              </Box>
            )}

            {/* Questions */}
            <Box sx={{ p: 2 }}>
              {group.questions.map((mcq, qIdx) => (
                <MCQReview
                  key={qIdx}
                  mcqData={mcq}
                  answers={answers}
                  onExplainRequest={onExplainRequest}
                  loadingExplanations={loadingExplanations}
                  explanations={explanations}
                  questionRefs={questionRefs}
                  onAddNote={onAddNote}
                />
              ))}
            </Box>
          </Card>
        ))}
      </Collapse>
    </Box>
  );
};

/* ---------------------------------------------------------------
   Part C Review Section
--------------------------------------------------------------- */
const PartCReview = ({
  screens,
  answers,
  onExplainRequest,
  loadingExplanations,
  explanations,
  questionRefs,
  onAddNote,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Part Header */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: BRAND_PRIMARY,
          borderRadius: 1.5,
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              Part C - Extended Listening
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Multiple choice questions
            </Typography>
          </Box>
          <IconButton sx={{ color: "#fff" }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Card>

      {/* Content */}
      <Collapse in={expanded}>
        {screens.map((screen, screenIdx) => (
          <Card
            key={screenIdx}
            elevation={0}
            sx={{
              mb: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1.5,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            {/* Extract Header */}
            <Box
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
              >
                {screen.extractName}
              </Typography>
            </Box>

            {/* Audio Player */}
            {screen.audioURL && (
              <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <AudioPlayer audioUrl={screen.audioURL} />
              </Box>
            )}

            {/* Instructions */}
            {screen.instructions && (
              <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1.5,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <InfoOutlined
                      sx={{ fontSize: 22, color: BRAND_PRIMARY, mr: 1 }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: BRAND_PRIMARY }}
                    >
                      Instruction
                    </Typography>
                  </Box>
                  <HtmlRenderer
                    content={screen.instructions}
                    sx={{
                      fontSize: "0.875rem",
                      color: "#555",
                      lineHeight: 1.6,
                    }}
                  />
                </Paper>
              </Box>
            )}

            {/* Questions */}
            <Box sx={{ p: 2 }}>
              {screen.contents?.map((mcq, qIdx) => (
                <MCQReview
                  key={qIdx}
                  mcqData={mcq}
                  answers={answers}
                  onExplainRequest={onExplainRequest}
                  loadingExplanations={loadingExplanations}
                  explanations={explanations}
                  questionRefs={questionRefs}
                  onAddNote={onAddNote}
                />
              ))}
            </Box>
          </Card>
        ))}
      </Collapse>
    </Box>
  );
};

/* ---------------------------------------------------------------
   Main Component
--------------------------------------------------------------- */
export default function ListeningSectionReview({
  listeningData,
  answers = [],
  onExplainRequest,
  loadingExplanations = {},
  explanations = {},
  // Notes props
  notes = [],
  onAddNote = null,
  notesModalOpen = false,
  setNotesModalOpen = null,
  // Module data for obtained marks
  moduleData = {},
}) {
  // Playlist derived from listening data
  const playlist = useMemo(() => buildPlaylist(listeningData), [listeningData]);

  // Refs for question DOM nodes used for scrolling/navigation
  const questionRefs = useRef({});

  // Ref to store quessection instead of part
  const sectionGroups = useMemo(() => {
    const groups = new Map();
    playlist.forEach((screen) => {
      const sectionKey = screen.sectionTitle || screen.extractName;
      if (!groups.has(sectionKey)) {
        groups.set(sectionKey, {
          sectionTitle: screen.sectionTitle || screen.extractName,
          sectionOrder: screen.sectionOrder,
          audioURL: screen.audioURL,
          screens: [],
        });
      }
      groups.get(sectionKey).screens.push(screen);
    });
    // Sort by section order
    return Array.from(groups.values()).sort(
      (a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0),
    );
  }, [playlist]);

  // Expanded state for each section
  const [expandedSections, setExpandedSections] = useState({});

  // Initialize all sections as expanded
  useEffect(() => {
    if (sectionGroups.length > 0) {
      const initialExpanded = {};
      sectionGroups.forEach((_, index) => {
        initialExpanded[index] = true;
      });
      setExpandedSections(initialExpanded);
    }
  }, [sectionGroups]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = answers.length;
    const correct = answers.filter((a) => a.isCorrect).length;
    const wrong = total - correct;
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    return { total, correct, wrong, percentage };
  }, [answers]);

  // Question chips with answered status
  const allQuestionIds = useMemo(() => {
    const ids = [];
    playlist.forEach((screen) => {
      if (
        screen.type === "noteCompletion" ||
        screen.type === "tableCompletion" ||
        screen.type === "formCompletion" ||
        screen.type === "flowchartCompletion"
      ) {
        ids.push(...(screen.questionIds || []));
      } else if (screen.type === "mcqSingle") {
        const qid = screen.content?.questionId;
        if (qid != null) ids.push(Number(qid));
      } else if (screen.type === "mcqMultiple") {
        // For MCQ multiple, include all question IDs from the group
        if (screen.questionIds && screen.questionIds.length > 0) {
          ids.push(...screen.questionIds.map((id) => Number(id)));
        } else {
          const qid = screen.content?.questionId;
          if (qid != null) ids.push(Number(qid));
        }
      } else if (screen.type === "mcqGroup") {
        ids.push(...(screen.questionIds || []));
      } else if (screen.type === "matchingInformation") {
        const qid = screen.content?.questionId;
        if (qid != null) ids.push(Number(qid));
      } else if (screen.type === "matchingFeature") {
        const qid = screen.content?.questionId;
        if (qid != null) ids.push(Number(qid));
      } else if (screen.type === "mapLabelling") {
        const qid = screen.content?.questionId;
        if (qid != null) ids.push(Number(qid));
      }
    });
    return ids;
  }, [playlist]);

  const chipStates = useMemo(() => {
    return allQuestionIds.map((qid) => {
      const answerData = answers.find((a) => Number(a.questionId) === qid);
      return {
        qid,
        isCorrect: answerData?.isCorrect ?? false,
      };
    });
  }, [allQuestionIds, answers]);

  // Handle chip click - scroll to question
  const handleChipClick = useCallback(
    (qid) => {
      // Find which section contains this question
      let sectionIndex = -1;
      for (let i = 0; i < sectionGroups.length; i++) {
        const section = sectionGroups[i];
        // Check if any screen in this section contains the question
        const containsQuestion = section.screens.some((screen) => {
          if (
            screen.type === "noteCompletion" ||
            screen.type === "tableCompletion" ||
            screen.type === "formCompletion" ||
            screen.type === "flowchartCompletion"
          ) {
            return screen.questionIds?.includes(qid);
          } else if (screen.type === "mcqSingle") {
            return Number(screen.content?.questionId) === qid;
          } else if (screen.type === "mcqMultiple") {
            if (screen.questionIds && screen.questionIds.length > 0) {
              return screen.questionIds.map((id) => Number(id)).includes(qid);
            }
            return Number(screen.content?.questionId) === qid;
          } else if (screen.type === "mcqGroup") {
            return screen.questionIds?.includes(qid);
          } else if (screen.type === "matchingInformation") {
            return Number(screen.content?.questionId) === qid;
          } else if (screen.type === "matchingFeature") {
            return Number(screen.content?.questionId) === qid;
          } else if (screen.type === "mapLabelling") {
            return Number(screen.content?.questionId) === qid;
          }
          return false;
        });
        if (containsQuestion) {
          sectionIndex = i;
          break;
        }
      }

      // If section is collapsed, expand it first
      if (sectionIndex >= 0 && !expandedSections[sectionIndex]) {
        setExpandedSections((prev) => ({
          ...prev,
          [sectionIndex]: true,
        }));
        // Wait for expansion animation before scrolling
        setTimeout(() => {
          scrollToQuestion(qid);
        }, 300);
      } else {
        scrollToQuestion(qid);
      }
    },
    [sectionGroups, expandedSections],
  );

  const scrollToQuestion = (qid) => {
    const el = questionRefs.current[qid];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Add a brief highlight effect
      el.style.transition = "box-shadow 0.3s ease";
      el.style.boxShadow = `0 0 0 3px ${BRAND_PRIMARY}`;
      setTimeout(() => {
        el.style.boxShadow = "none";
      }, 1500);
    }
  };

  if (!playlist.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No listening data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#fff" }}>
      {/* Header - Similar to Original */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
          }}
        >
          {/* Title and Stats Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: BRAND_PRIMARY,
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                }}
              >
                Listening Module Review
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#666",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                }}
              >
                Review your answers
              </Typography>
            </Box>
            {/* Notes Button */}
            {setNotesModalOpen && (
              <Tooltip title="View Notes" placement="left" arrow>
                <IconButton
                  onClick={() => setNotesModalOpen(true)}
                  sx={{
                    bgcolor: BRAND_PRIMARY,
                    color: "#fff",
                    width: 36,
                    height: 36,
                    "&:hover": {
                      bgcolor: BRAND_DARK,
                    },
                  }}
                >
                  <Badge
                    badgeContent={notes?.length || 0}
                    color="error"
                    overlap="circular"
                  >
                    <StickyNote2Icon sx={{ fontSize: 18 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Question Chips - Clickable */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {chipStates.map(({ qid, isCorrect }) => (
              <Box
                key={qid}
                onClick={() => handleChipClick(qid)}
                sx={{
                  width: { xs: 22, sm: 26 },
                  height: { xs: 22, sm: 26 },
                  borderRadius: "50%",
                  border: `2px solid ${isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY}`,
                  bgcolor: "#fff",
                  color: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: isCorrect ? SUCCESS_GREEN : BRAND_PRIMARY,
                    color: "#fff",
                    transform: "scale(1.1)",
                  },
                }}
              >
                {qid}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Body Content */}
      <Box
        sx={{
          p: { xs: 1, sm: 1.5 },
          width: "100%",
        }}
      >
        {/* Render each section separately */}
        {sectionGroups.map((sectionGroup, idx) => (
          <SectionReview
            key={idx}
            sectionTitle={sectionGroup.sectionTitle}
            sectionOrder={sectionGroup.sectionOrder}
            audioURL={sectionGroup.audioURL}
            screens={sectionGroup.screens}
            answers={answers}
            onExplainRequest={onExplainRequest}
            loadingExplanations={loadingExplanations}
            explanations={explanations}
            questionRefs={questionRefs}
            expanded={expandedSections[idx] ?? true}
            onAddNote={onAddNote}
            onToggleExpand={() =>
              setExpandedSections((prev) => ({
                ...prev,
                [idx]: !prev[idx],
              }))
            }
          />
        ))}
      </Box>

      {/* Notes Modal */}
      {setNotesModalOpen && (
        <NotesModal
          open={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          notes={notes}
        />
      )}
    </Box>
  );
}
